<template>
  <div class="sg-root">
    <!-- Header -->
    <header class="sg-header">
      <h1 class="sg-title">{{ t.title }}</h1>
      <p class="sg-subtitle">{{ t.subtitle }}</p>
    </header>

    <!-- Search -->
    <div ref="searchBox" class="sg-search">
      <div class="sg-search-field" :class="{ 'is-focused': dropdownOpen }">
        <i class="fas fa-search sg-search-icon" aria-hidden="true"></i>
        <input
          v-model="query"
          type="text"
          class="sg-search-input"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="showDropdown ? 'true' : 'false'"
          :placeholder="t.placeholder"
          :aria-label="t.placeholder"
          @input="onInput"
          @focus="onFocus"
          @keydown.down.prevent="moveActive(1)"
          @keydown.up.prevent="moveActive(-1)"
          @keydown.enter.prevent="onEnter"
          @keydown.esc="closeDropdown"
        />
        <button
          v-if="query"
          type="button"
          class="sg-search-clear"
          :aria-label="t.clear"
          @click="clearSearch"
        >
          <i class="fas fa-times-circle" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Live results dropdown -->
      <div v-if="showDropdown" class="sg-dropdown">
        <template v-if="results.length">
          <div class="sg-dropdown-head">
            <span class="sg-dropdown-label">{{ topMatchesLabel }}</span>
            <a href="#" class="sg-dropdown-all" @click.prevent="viewAll">{{
              t.viewAll
            }}</a>
          </div>
          <ul class="sg-results" role="listbox">
            <li
              v-for="(r, i) in topResults"
              :key="r.child.id"
              class="sg-result"
              :class="{ 'is-active': i === activeIndex }"
              role="option"
              :aria-selected="i === activeIndex ? 'true' : 'false'"
              @mouseenter="activeIndex = i"
              @mousedown.prevent="goTo(r)"
            >
              <span class="sg-result-icon">
                <i :class="r.child.icon" aria-hidden="true"></i>
              </span>
              <span class="sg-result-text">
                <span class="sg-result-line">
                  <strong class="sg-result-title">{{ r.title }}</strong>
                  <span
                    v-for="(c, ci) in r.crumbs"
                    :key="ci"
                    class="sg-result-crumb"
                  >
                    <span class="sg-result-sep">›</span> {{ c }}
                  </span>
                </span>
                <span class="sg-result-snippet" v-html="r.snippet"></span>
              </span>
            </li>
          </ul>
        </template>
        <div v-else class="sg-empty">{{ t.noResults }}</div>
      </div>
    </div>
    <div class="alert alert-primary mx-5 d-flex align-items-start" role="alert">
      <i class="fas fa-info-circle mr-3 mt-1" aria-hidden="true"></i>
      <span>{{ t.guide }}</span>
    </div>

    <!-- "View all results" banner -->
    <div v-if="filtering" class="sg-filter-bar">
      <span>{{ resultsForLabel }}</span>
      <button type="button" class="sg-link-btn" @click="clearSearch">
        {{ t.clear }}
      </button>
    </div>

    <!-- Sections -->
    <main class="sg-main px-5">
      <p
        v-if="filtering && !visibleParents.length"
        class="sg-empty sg-empty-page"
      >
        {{ t.noResults }}
      </p>

      <section
        v-for="parent in visibleParents"
        :key="parent.id"
        class="sg-group"
      >
        <h2 class="sg-group-title">
          <i
            :class="parentIcon(parent)"
            class="sg-group-icon"
            aria-hidden="true"
          ></i>
          {{ cleanTitle(parent.header) }}
        </h2>

        <div class="sg-card">
          <div
            v-for="child in parent.children"
            :id="'sg-item-' + child.id"
            :key="child.id"
            class="sg-item"
            :class="{ 'is-open': isOpen(child.id) }"
          >
            <button
              type="button"
              class="sg-item-head"
              :aria-expanded="isOpen(child.id) ? 'true' : 'false'"
              :aria-controls="'sg-panel-' + child.id"
              @click="toggle(child.id)"
            >
              <i
                :class="child.icon"
                class="sg-item-icon"
                aria-hidden="true"
              ></i>
              <span class="sg-item-title">{{ child.header }}</span>
              <i class="fas fa-chevron-down sg-chevron" aria-hidden="true"></i>
            </button>

            <transition
              name="sg-collapse"
              @before-enter="onBeforeEnter"
              @enter="onPanelEnter"
              @after-enter="onAfterEnter"
              @before-leave="onBeforeLeave"
              @leave="onLeave"
            >
              <div
                v-if="isOpen(child.id)"
                :id="'sg-panel-' + child.id"
                class="sg-panel"
              >
                <div class="sg-body">
                  <p v-if="child.description" class="sg-desc">
                    {{ child.description }}
                  </p>
                  <div
                    v-if="child.content"
                    class="sg-content"
                    v-html="child.content"
                  ></div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </section>
    </main>

    <!-- Back -->
    <div class="row mt-4">
      <div class="col-lg-12 text-center">
        <router-link to="/dashboard" class="btn btn-danger">{{
          $t("help.back")
        }}</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import en from "./start-guide-en.json";
import ja from "./start-guide-ja.json";

const LOCALES = { en, ja };

const LABELS = {
  en: {
    title: "Touchless Check-in Start Guide",
    subtitle: "Find everything you need to get started",
    placeholder: "Search the starting guide...",
    topMatches: "Top matches ({n})",
    viewAll: "View all results",
    noResults: "No matching topics found. Try a different keyword.",
    resultsFor: '{n} result(s) for "{q}"',
    clear: "Clear",
    back: "Back",
    guide:
      "This guide is intended for both users who have implemented customization, such as linking the touchless check-in system to a PMS, and users who have not implemented customization. Please note that some features are only available to users who have implemented the customization.",
  },
  ja: {
    title: "タッチレスチェックイン スタートガイド",
    subtitle: "はじめに必要な情報がすべて見つかります",
    placeholder: "スタートガイドを検索...",
    topMatches: "上位の検索結果 ({n})",
    viewAll: "すべての結果を表示",
    noResults: "一致する項目がありません。別のキーワードをお試しください。",
    resultsFor: "「{q}」の検索結果 {n} 件",
    clear: "クリア",
    back: "戻る",
    guide:
      "このガイドはタッチレスチェックインシステムをPMSに連携などカスタマイズを実装したユーザーと、カスタマイズを実装していないユーザーの利用両方を対象に記載しております。カスタマイズを実装されたユーザーだけが利用できる機能の説明もありますので、ご了承ください。",
  },
};

const PARENT_ICONS = {
  "first-time-setup": "fas fa-cog",
  "useful-functions": "fas fa-lightbulb",
};

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Turns the HTML stored in "content" into plain text for searching.
// HTML comments (e.g. the commented-out Batch Process block) are dropped.
function htmlToText(html) {
  if (!html) return "";
  const spaced = html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(li|p|div|tr|td|th|h\d)>/gi, "$& ");
  let text;
  if (typeof document !== "undefined") {
    const el = document.createElement("div");
    el.innerHTML = spaced;
    text = el.textContent || "";
  } else {
    text = spaced.replace(/<!--[\s\S]*?-->/g, "").replace(/<[^>]+>/g, "");
  }
  return text.replace(/\s+/g, " ").trim();
}

export default {
  name: "StartingGuide",

  props: {
    // Optional override ('en' or 'ja'). When omitted, the component follows
    // $i18n.locale (vue-i18n), so it switches language with the rest of the app.
    locale: {
      type: String,
      default: "",
    },
    // How many rows the live dropdown shows
    maxSuggestions: {
      type: Number,
      default: 4,
    },
  },

  data() {
    const sections = LOCALES[this.resolveLang()] || en;
    const first =
      sections[0] && sections[0].children && sections[0].children[0];
    return {
      query: "",
      dropdownOpen: false,
      activeIndex: -1,
      filterMode: false, // true after "View all results"
      openIds: [],
    };
  },

  computed: {
    // 'en' | 'ja' — reacts to $i18n.locale changes
    lang() {
      return this.resolveLang();
    },

    sections() {
      return LOCALES[this.lang] || en;
    },

    t() {
      return LABELS[this.lang] || LABELS.en;
    },

    trimmedQuery() {
      return this.query.trim();
    },

    // Searchable copy of every child (built once per locale)
    searchIndex() {
      const rows = [];
      this.sections.forEach((parent) => {
        parent.children.forEach((child) => {
          const header = child.header || "";
          const desc = child.description || "";
          const body = htmlToText(child.content);
          const full = (desc + " " + body).trim();
          rows.push({
            parent,
            child,
            header,
            headerLower: header.toLowerCase(),
            full,
            fullLower: full.toLowerCase(),
          });
        });
      });
      return rows;
    },

    // Ranked matches: title hits first, then text hits, keeping guide order
    results() {
      const q = this.trimmedQuery.toLowerCase();
      if (!q) return [];
      const out = [];
      this.searchIndex.forEach((row, order) => {
        const inHeader = row.headerLower.indexOf(q);
        const inText = row.fullLower.indexOf(q);
        if (inHeader === -1 && inText === -1) return;

        const parentTitle = this.cleanTitle(row.parent.header).replace(
          /^\d+\.\s*/,
          ""
        );
        if (inHeader !== -1) {
          out.push({
            child: row.child,
            order,
            score: inHeader === 0 ? 200 : 100,
            title: row.header,
            crumbs: [parentTitle],
            snippet: this.makeSnippet(row.full, q),
          });
        } else {
          const found = row.full.substr(inText, q.length);
          out.push({
            child: row.child,
            order,
            score: 10,
            title: found.charAt(0).toUpperCase() + found.slice(1),
            crumbs: [row.header, parentTitle],
            snippet: this.makeSnippet(row.full, q),
          });
        }
      });
      return out.sort((a, b) => b.score - a.score || a.order - b.order);
    },

    topResults() {
      return this.results.slice(0, this.maxSuggestions);
    },

    showDropdown() {
      return this.dropdownOpen && !!this.trimmedQuery;
    },

    filtering() {
      return this.filterMode && !!this.trimmedQuery;
    },

    topMatchesLabel() {
      return this.t.topMatches.replace("{n}", this.topResults.length);
    },

    resultsForLabel() {
      return this.t.resultsFor
        .replace("{n}", this.results.length)
        .replace("{q}", this.trimmedQuery);
    },

    // Sections shown on the page (narrowed to matches after "View all results")
    visibleParents() {
      if (!this.filtering) return this.sections;
      const ids = {};
      this.results.forEach((r) => {
        ids[r.child.id] = true;
      });
      return this.sections
        .map((p) =>
          Object.assign({}, p, {
            children: p.children.filter((c) => ids[c.id]),
          })
        )
        .filter((p) => p.children.length);
    },
  },

  watch: {
    lang() {
      const first = this.sections[0] && this.sections[0].children[0];
      this.openIds = [];
      this.clearSearch();
    },
  },

  mounted() {
    document.addEventListener("mousedown", this.onDocMouseDown);
  },

  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocMouseDown);
  },

  methods: {
    /* ---------- language ---------- */
    resolveLang() {
      const raw = this.locale || (this.$i18n && this.$i18n.locale) || "en";
      const short = String(raw).toLowerCase().split(/[-_]/)[0]; // 'ja-JP' -> 'ja'
      return Object.prototype.hasOwnProperty.call(LOCALES, short)
        ? short
        : "en";
    },

    /* ---------- display helpers ---------- */
    cleanTitle(s) {
      return String(s || "").replace(/\s*[:：]\s*$/, "");
    },

    parentIcon(parent) {
      return PARENT_ICONS[parent.id] || "fas fa-folder";
    },

    makeSnippet(text, q, radius) {
      radius = radius || 60;
      const idx = text.toLowerCase().indexOf(q);
      if (idx === -1) {
        const head = text.slice(0, radius * 2);
        return escapeHtml(head) + (text.length > head.length ? "…" : "");
      }
      const start = Math.max(0, idx - radius);
      const end = Math.min(text.length, idx + q.length + radius);
      return (
        (start > 0 ? "…" : "") +
        this.highlight(text.slice(start, end), q) +
        (end < text.length ? "…" : "")
      );
    },

    // Escapes the text and wraps every match of q in <mark>
    highlight(text, q) {
      const lower = text.toLowerCase();
      let html = "";
      let pos = 0;
      let idx = lower.indexOf(q, pos);
      while (idx !== -1) {
        html += escapeHtml(text.slice(pos, idx));
        html += "<mark>" + escapeHtml(text.substr(idx, q.length)) + "</mark>";
        pos = idx + q.length;
        idx = lower.indexOf(q, pos);
      }
      return html + escapeHtml(text.slice(pos));
    },

    /* ---------- accordion ---------- */
    isOpen(id) {
      return this.openIds.indexOf(id) !== -1;
    },

    toggle(id) {
      const i = this.openIds.indexOf(id);
      if (i === -1) this.openIds.push(id);
      else this.openIds.splice(i, 1);
    },

    onBeforeEnter(el) {
      el.style.height = "0";
    },
    onPanelEnter(el) {
      el.style.height = el.scrollHeight + "px";
    },
    onAfterEnter(el) {
      el.style.height = "";
    },
    onBeforeLeave(el) {
      el.style.height = el.scrollHeight + "px";
    },
    onLeave(el) {
      void el.offsetHeight; // force reflow so the height change animates
      el.style.height = "0";
    },

    /* ---------- search ---------- */
    onInput() {
      this.dropdownOpen = true;
      this.filterMode = false;
      this.activeIndex = -1;
    },

    onFocus() {
      if (this.trimmedQuery) this.dropdownOpen = true;
    },

    closeDropdown() {
      this.dropdownOpen = false;
      this.activeIndex = -1;
    },

    onDocMouseDown(e) {
      const box = this.$refs.searchBox;
      if (box && !box.contains(e.target)) this.closeDropdown();
    },

    moveActive(dir) {
      const n = this.topResults.length;
      if (!n) return;
      this.dropdownOpen = true;
      if (this.activeIndex === -1) this.activeIndex = dir > 0 ? 0 : n - 1;
      else this.activeIndex = (this.activeIndex + dir + n) % n;
    },

    onEnter() {
      const r = this.topResults[this.activeIndex];
      if (r) this.goTo(r);
      else if (this.trimmedQuery) this.viewAll();
    },

    clearSearch() {
      this.query = "";
      this.filterMode = false;
      this.openIds = []; // Closed all collapsed tabs
      this.closeDropdown();
    },

    // Open one section and scroll to it
    goTo(result) {
      const id = result.child.id;
      if (!this.isOpen(id)) this.openIds.push(id);
      this.closeDropdown();
      this.$nextTick(() => {
        const el = document.getElementById("sg-item-" + id);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },

    // Show every match on the page, all expanded
    viewAll() {
      if (!this.results.length) return;
      this.filterMode = true;
      this.openIds = this.results.map((r) => r.child.id);
      this.closeDropdown();
    },
  },
};
</script>

<style>
/* Unscoped on purpose: the guide content is injected with v-html.
   Every selector is prefixed with "sg-" to avoid leaking. */

.sg-root {
  --sg-blue: #2563eb;
  --sg-blue-soft: #eff6ff;
  --sg-text: #1f2937;
  --sg-muted: #6b7280;
  --sg-border: #e5e7eb;
  --sg-red: #ef4444;

  box-sizing: border-box;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 24px 16px 40px;
  color: var(--sg-text);
  font-size: 14px;
  line-height: 1.6;
}

/* Header */
.sg-header {
  text-align: center;
  margin-bottom: 20px;
}
.sg-title {
  margin: 0;
  font-size: 25px !important;
  font-weight: 700 !important;
}
.sg-subtitle {
  margin: 4px 0 0;
  color: var(--sg-blue);
  opacity: 0.85;
}

/* Search */
.sg-search {
  position: relative;
  max-width: 620px;
  margin: 0 auto 28px;
  z-index: 10;
}
.sg-search-field {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 12px;
  background: #fff;
  border: 1px solid var(--sg-border);
  border-radius: 8px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.sg-search-field.is-focused,
.sg-search-field:focus-within {
  border-color: var(--sg-blue);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.sg-search-icon {
  color: var(--sg-blue);
  margin-right: 10px;
}
.sg-search-input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font: inherit;
  color: var(--sg-text);
}
.sg-search-clear {
  border: 0;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  font-size: 16px;
}
.sg-search-clear:hover {
  color: var(--sg-text);
}

/* Dropdown */
.sg-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  background: #fff;
  border: 1px solid var(--sg-border);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
  padding: 8px;
  max-height: 70vh;
  overflow-y: auto;
}
.sg-dropdown-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 8px 8px;
  font-size: 13px;
}
.sg-dropdown-label {
  font-weight: 600;
  color: var(--sg-muted);
}
.sg-dropdown-all {
  color: var(--sg-blue);
  font-weight: 600;
  text-decoration: none;
}
.sg-dropdown-all:hover {
  text-decoration: underline;
}
.sg-results {
  list-style: none;
  margin: 0;
  padding: 0;
}
.sg-result {
  display: flex;
  gap: 10px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
}
.sg-result.is-active {
  background: var(--sg-blue-soft);
}
.sg-result-icon {
  flex: 0 0 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: var(--sg-blue-soft);
  color: var(--sg-blue);
  font-size: 13px;
}
.sg-result.is-active .sg-result-icon {
  background: #fff;
}
.sg-result-text {
  min-width: 0;
  display: block;
}
.sg-result-line {
  display: block;
}
.sg-result-title {
  font-weight: 700;
}
.sg-result-crumb,
.sg-result-sep {
  color: var(--sg-muted);
  font-size: 12.5px;
}
.sg-result-snippet {
  display: block;
  color: var(--sg-muted);
  font-size: 12.5px;
  line-height: 1.45;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.sg-result-snippet mark {
  background: #fef3c7;
  color: inherit;
  padding: 0 1px;
  border-radius: 2px;
}
.sg-empty {
  padding: 14px 10px;
  color: var(--sg-muted);
}
.sg-empty-page {
  text-align: center;
}

/* View-all banner */
.sg-filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: var(--sg-blue-soft);
  border-radius: 8px;
  color: var(--sg-text);
}
.sg-link-btn {
  border: 0;
  background: transparent;
  color: var(--sg-blue);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

/* Groups & accordion */
.sg-group {
  margin-bottom: 26px;
}
.sg-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 18px !important;
  font-weight: 700 !important;
}
.sg-group-icon {
  color: var(--sg-blue);
}
.sg-card {
  background: #fff;
  border: 1px solid var(--sg-border);
  border-radius: 8px;
  overflow: hidden;
}
.sg-item + .sg-item {
  border-top: 1px solid var(--sg-border);
}
.sg-item-head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 14px;
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 600;
  text-align: left;
  color: var(--sg-text);
  cursor: pointer;
}
.sg-item-head:hover {
  background: #f9fafb;
}
.sg-item-head:focus-visible {
  outline: 2px solid var(--sg-blue);
  outline-offset: -2px;
}
.sg-item-icon {
  color: var(--sg-blue);
}
.sg-item-title {
  flex: 1;
}
.sg-item.is-open .sg-item-title {
  color: var(--sg-blue);
}
.sg-chevron {
  color: var(--sg-muted);
  font-size: 12px;
  transition: transform 0.25s ease;
}
.sg-item.is-open .sg-chevron {
  transform: rotate(180deg);
}

/* Collapse animation (height is driven from the component) */
.sg-panel {
  overflow: hidden;
}
.sg-collapse-enter-active,
.sg-collapse-leave-active {
  transition: height 0.25s ease;
  overflow: hidden;
}
@media (prefers-reduced-motion: reduce) {
  .sg-collapse-enter-active,
  .sg-collapse-leave-active,
  .sg-chevron {
    transition: none;
  }
}

/* Section body */
.sg-body {
  padding: 4px 18px 16px 40px;
  font-size: 13px;
  color: #374151;
}
.sg-desc {
  margin: 0 0 10px;
}
.sg-content p {
  margin: 0 0 10px;
}
.sg-content ul {
  margin: 4px 0;
  padding-left: 20px;
}
.sg-content li {
  margin: 3px 0;
}
.sg-content em {
  color: var(--sg-muted);
}
.sg-content .table-responsive {
  overflow-x: auto;
}
.sg-content table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}
.sg-content th,
.sg-content td {
  border: 1px solid var(--sg-border);
  padding: 6px 8px;
  vertical-align: top;
}
.sg-content thead th {
  background: var(--sg-blue-soft);
  text-align: center;
}
.sg-content .text-center {
  text-align: center;
}
@media (max-width: 560px) {
  .sg-body {
    padding-left: 18px;
  }
}
</style>